
import { isSSR } from '@/lib/isSSR';
import { BlogCacheItem } from './types';

// TTL in milliseconds (15 minutes - reduced from 30 minutes to be more responsive to changes)
export const CACHE_TTL = 15 * 60 * 1000;

// Debug mode for cache operations
export const CACHE_DEBUG = process.env.NODE_ENV === 'development';

// Server cache store - In-memory cache that will be reset on server restart
interface ServerCache {
  posts: {
    [key: string]: BlogCacheItem<any[]>;
  };
  singlePosts: {
    [slug: string]: BlogCacheItem<any>;
  };
}

// Initialize server cache
export const serverCache: ServerCache = {
  posts: {},
  singlePosts: {}
};

/**
 * Check if a cache item is valid (not expired)
 */
export const isCacheValid = (cacheItem: BlogCacheItem<any>): boolean => {
  const now = Date.now();
  return (now - cacheItem.timestamp) < CACHE_TTL;
};

/**
 * Get cache directory path
 */
export const getCacheDir = (): string => {
  if (!isSSR()) return '';
  
  try {
    if (typeof process !== 'undefined' && process.versions && process.versions.node) {
      const path = require('path');
      return path.resolve(process.cwd(), '.cache/blog');
    }
    return '';
  } catch (error) {
    console.error('Error getting cache directory:', error);
    return '';
  }
};

/**
 * Ensure cache directory exists
 */
export const ensureCacheDir = (): void => {
  if (!isSSR()) return;
  
  try {
    if (typeof process !== 'undefined' && process.versions && process.versions.node) {
      const fs = require('fs');
      const cacheDir = getCacheDir();
      if (!cacheDir) return;
      
      if (!fs.existsSync(cacheDir)) {
        fs.mkdirSync(cacheDir, { recursive: true });
      }
    }
  } catch (error) {
    console.error('Error ensuring cache directory:', error);
  }
};

/**
 * Clear all blog cache
 */
export const clearBlogCache = (): void => {
  serverCache.posts = {};
  serverCache.singlePosts = {};
  
  if (CACHE_DEBUG) {
    console.log('[Blog Cache] Cleared all cache entries');
  }
};

/**
 * Clear category-related cache entries
 */
export const clearCategoryCaches = (): void => {
  const categoryPattern = /_category_/;
  
  // Count for logging
  let clearedCount = 0;
  
  // Clear category entries from posts cache
  Object.keys(serverCache.posts).forEach(key => {
    if (categoryPattern.test(key)) {
      delete serverCache.posts[key];
      clearedCount++;
    }
  });
  
  if (CACHE_DEBUG && clearedCount > 0) {
    console.log(`[Blog Cache] Cleared ${clearedCount} category cache entries`);
  }
};

// Updated interface to include memoryUsage
interface CacheStats {
  posts: number; 
  singlePosts: number;
  postsKeys: string[];
  singlePostsKeys: string[];
  totalSize: number;
  creationTime: Date;
  memoryUsage?: NodeJS.MemoryUsage | null;
}

/**
 * Get cache stats for API and monitoring
 */
export const getCacheStats = (): CacheStats => {
  // Calculate total size (approximate)
  let totalSize = 0;
  
  // Calculate size for posts
  Object.values(serverCache.posts).forEach(item => {
    try {
      const itemSize = JSON.stringify(item).length;
      totalSize += itemSize;
    } catch (e) {
      // Skip if can't stringify
    }
  });
  
  // Calculate size for single posts
  Object.values(serverCache.singlePosts).forEach(item => {
    try {
      const itemSize = JSON.stringify(item).length;
      totalSize += itemSize;
    } catch (e) {
      // Skip if can't stringify
    }
  });
  
  // Get current process stats if available
  let memoryUsage = null;
  if (typeof process !== 'undefined' && process.memoryUsage) {
    try {
      memoryUsage = process.memoryUsage();
    } catch (e) {
      // Ignore if not available
    }
  }
  
  return {
    posts: Object.keys(serverCache.posts).length,
    singlePosts: Object.keys(serverCache.singlePosts).length,
    postsKeys: Object.keys(serverCache.posts),
    singlePostsKeys: Object.keys(serverCache.singlePosts),
    totalSize: totalSize,
    creationTime: new Date(),
    memoryUsage
  };
};

/**
 * Check if cache entry exists (for debugging)
 */
export const hasCacheEntry = (key: string): boolean => {
  return !!serverCache.posts[key] || !!serverCache.singlePosts[key];
};

